import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderTakes } from "../lib/takeOrder";
import { anchored } from "../lib/anchor";
import { formatBytes, formatTime } from "../lib/format";
import { api, downloadViaUrl } from "../api/client";
import type { AudioAssetDto, Boundary, ChapterSummary } from "../api/types";
import type { ReviewTally } from "../lib/reportStore";
import { useUploads } from "../lib/uploads";
import { useReplaceJobs } from "../lib/replaceJobs";
import { useBoundaryReproof } from "../lib/useBoundaryReproof";
import { ScriptReproofBanner, useScriptCheck } from "./ScriptReproofBanner";
import { BoundaryCard } from "./BoundaryCard";
import { Icon } from "./icons";
import { toast } from "./toast";
import { UploadIntentDialog } from "./UploadIntentDialog";
import { RowTransport, useRowPlayer } from "./RowPlayer";
import { StartProofingDialog, parkedFiles } from "./StartProofingDialog";
import { DeleteFilesDialog, type Doomed } from "./DeleteFilesDialog";
import { ReplaceFileSetDialog } from "./ReplaceFileSetDialog";
import { LABELS, labelDot, labelName } from "../lib/labels";
import { NoiseAnalyticsDialog } from "./NoiseAnalyticsDialog";
import { useMe } from "../lib/me";
import { TransferAudioDialog } from "./TransferAudioDialog";
import { backdropClose } from "../lib/backdrop";

const TAKES_WIDE_KEY = "hub_takes_wide";
const TAKES_NOTES_KEY = "hub_takes_notes";
const AUDIO_RE = /\.(wav|mp3|m4a|flac|ogg|webm)$/i;

function ReviewCell({ t }: { t?: ReviewTally }) {
  if (!t || t.total === 0) return <span className="muted">—</span>;
  const done = t.picked + t.dismissed;
  return (
    <span className="ch-review" title={`${done}/${t.total} reviewed — ${t.picked} picked · ${t.dismissed} dismissed · ${t.unreviewed} unreviewed`}>
      <span className={`crv picked${t.picked ? "" : " zero"}`}>★ {t.picked}</span>
      <span className={`crv dismissed${t.dismissed ? "" : " zero"}`}>✕ {t.dismissed}</span>
      <span className={`crv unreviewed${t.unreviewed ? "" : " zero"}`}>• {t.unreviewed}{t.unreviewed ? " unreviewed" : ""}</span>
    </span>
  );
}

/** The version tag beside a filename.
 *
 *  Only rendered where more than one version exists, so the tag itself carries
 *  the information "there is something to switch between" — a badge on every
 *  row would say nothing. Clicking it flips which version the project follows;
 *  nothing is moved and nothing is deleted, because each version keeps its own
 *  bytes under its own key.
 */

/** The swatch on a row: a filled dot when labelled, a hollow ring when not, so
 *  an unlabelled file still shows WHERE the label goes rather than hiding the
 *  affordance until you happen to hover the right pixel. */
function LabelSwatch({ color, onPick }: { color?: string; onPick: (rect: DOMRect) => void }) {
  const dot = labelDot(color);
  return (
    <button type="button" className={`lbl-swatch${dot ? " on" : ""}`}
      style={dot ? { background: dot, borderColor: dot } : undefined}
      title={`${labelName(color)} — click to change`}
      aria-label={`Label: ${labelName(color)}`}
      onClick={(e) => { e.stopPropagation(); onPick((e.currentTarget as HTMLElement).getBoundingClientRect()); }} />
  );
}

/** Notes editor, same shape as the board's: a textarea because this is prose,
 *  saving on ⌘↵ or the tick rather than per keystroke, Escape to abandon. */
function NoteEditor({ value, label, onSave, onClose }: {
  value: string; label: string; onSave: (v: string) => void; onClose: () => void;
}) {
  const [v, setV] = useState(value);
  const done = () => { if (v !== value) onSave(v); else onClose(); };
  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>Note</h3>
        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>{label}</p>
          <textarea className="input takes-notearea" autoFocus rows={5} value={v}
            placeholder="What's going on with this file…"
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); onClose(); }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); done(); }
            }} />
        </div>
        <div className="modal-actions">
          <span className="muted" style={{ marginRight: "auto", fontSize: 11 }}>⌘↵ to save · Esc to discard</span>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={done}>Save</button>
        </div>
      </div>
    </div>
  );
}

function VersionTag({ a, busy, onPromote }: {
  a: AudioAssetDto;
  busy: boolean;
  onPromote: (filename: string) => void;
}) {
  // Anchored to the viewport from the button's own rect, because the takes card
  // clips its overflow to keep the table's corners rounded — a menu positioned
  // inside the row simply disappears at the card's edge.
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const versions = a.versions ?? [];
  if (versions.length < 2) return null;
  const open = at !== null;
  const toggle = () => {
    if (open) { setAt(null); return; }
    const r = btn.current?.getBoundingClientRect();
    if (r) setAt({ top: r.bottom + 6, left: r.left });
  };
  return (
    <span className="vtag-wrap">
      <button type="button" ref={btn} className={`vtag${open ? " open" : ""}`} disabled={busy}
        title={`${versions.length} versions of this file — click to choose which one the project uses`}
        onClick={toggle}>
        V{a.version ?? 1}
        <Icon name="chevron-down" size={11} />
      </button>
      {open && (
        <>
          <span className="vtag-scrim" onClick={() => setAt(null)} />
          <span className="vtag-menu" style={{ top: at.top, left: at.left }}>
            <span className="vtag-head">Official version</span>
            {versions.map((v) => (
              <button type="button" key={v.filename} className={`vtag-item${v.is_current ? " on" : ""}`}
                disabled={busy || v.is_current}
                onClick={() => { setAt(null); onPromote(v.filename); }}>
                <span className="vtag-check">
                  {v.is_current ? <Icon name="check" size={13} /> : null}
                </span>
                <span className="vtag-body">
                  <span className="vtag-name">V{v.version} <em>{v.filename}</em></span>
                  <span className="vtag-meta">
                    {new Date(v.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                    {" · "}{formatBytes(v.size_bytes)}
                    {v.status === "failed" ? " · failed" : ""}
                  </span>
                </span>
              </button>
            ))}
            <span className="vtag-note">
              The official version is the one every count, report, check and download
              uses - the project holds one file per chapter, updated, not two. Each
              version keeps the proof it was built on, so switching back brings that
              report with it, and pickups follow the official file.
            </span>
          </span>
        </>
      )}
    </span>
  );
}

function StatusCell({ a, ch, proofingActive, reproofing, notesAsText }: { a: AudioAssetDto; ch?: ChapterSummary; proofingActive: boolean; reproofing?: boolean; notesAsText?: boolean }) {
  if (a.status === "failed") return <span className="take-failed" title={a.error || ""}>Failed</span>;
  // Parked: a neutral drop (uploaded with auto-proof off). Not queued — waiting
  // for the user to start Proofing, or to run a Spec check (no proofing needed).
  const parked = a.status === "pending" && a.proof_requested === false;
  // A replacement that kept its predecessor's proofing is parked too - it was
  // never transcribed itself - but the proof it carries is real, so the row
  // says so rather than reading as a file nobody has started.
  const carried = parked && ch?.state === "proofed";
  if (parked && !carried)
    return <span className="muted" title="Uploaded - proofing not started. Use “Start proofing” above, or run a Spec check.">Uploaded - not started</span>;
  if (!carried && (a.status === "pending" || a.status === "transcribing"))
    return <span className="take-working"><span className="spinner" style={{ width: 12, height: 12 }} /> Transcribing…</span>;
  if (ch?.proof_excluded)
    return <span className="take-excluded" title="Excluded from proofing — not compared to the manuscript. Still checked in Spec check."><Icon name="ban" size={14} /> Excluded</span>;
  // A boundary re-proof re-runs an already-proofed chapter, so this takes
  // priority over "Proofed" until it finishes (then the row refreshes).
  if (reproofing)
    return <span className="take-working"><span className="spinner" style={{ width: 12, height: 12 }} /> Re-proofing…</span>;
  // The audio under this proof was replaced by a delivery of a different
  // runtime. The work was kept - most of it is still right - but its timestamps
  // no longer point where they did, so the row says so rather than reading
  // "Proofed" as if nothing had happened.
  if (ch?.state === "proofed" && ch.reproof_needed)
    return (
      <span className="take-stale" title="The audio was replaced by a file of a different length. The proofing and pickups were kept, but their timestamps are from the old file — re-proof this chapter to bring them back in line.">
        <Icon name="alert" size={14} /> Re&#8209;proof needed
      </span>
    );
  // Proofed while the script's notes still counted as the book's words: some of
  // its flags are the notes. The prompt above the list re-proofs it.
  if (ch?.state === "proofed" && notesAsText)
    return (
      <span className="take-stale" title="Proofed while the script's notes, comments or crossed-out words still counted as the book's text, so some of its flags are just the notes. Re-proof it with the cleaned script — the prompt above this list.">
        <Icon name="alert" size={14} /> Re&#8209;proof suggested
      </span>
    );
  if (ch?.state === "proofed" && carried)
    return (
      <span className="take-ready" title="The proofing and pickups of the version this file replaced were applied to it.">
        <Icon name="check" size={14} /> Proof applied
      </span>
    );
  if (ch?.state === "proofed") return <span className="take-ready"><Icon name="check" size={14} /> Proofed</span>;
  // Transcribed but not proofed: a live worker → genuinely proofing; otherwise the
  // proof stalled (the worker died or errored) — show that instead of an eternal
  // spinner, so the user knows to hit "Resume proofing".
  if (proofingActive)
    return <span className="take-working"><span className="spinner" style={{ width: 12, height: 12 }} /> Proofing…</span>;
  return <span className="take-stalled" title="Proofing didn’t finish for this audio file — use “Resume proofing” above to retry.">⚠ Stalled</span>;
}

/**
 * One persistent, file-centric list: a dropzone you keep adding to + one row per
 * audio file carrying its whole lifecycle inline (uploading % → Transcribing… →
 * Proofing… → Proofed ✓ + Report). Joins server assets (transcription status)
 * with the project's chapters (proof state) by filename. Drag-to-reorder the
 * proofed files; they auto-proof in the background (no proof buttons here).
 */
export function TakesList({
  projectId,
  projectName,
  hasManuscript,
  chapters,
  reviewByChapter,
  proofingActive,
  reproofingChapterId,
  onReorder,
}: {
  projectId: string;
  projectName: string;
  hasManuscript: boolean;
  chapters: ChapterSummary[];
  reviewByChapter: Record<string, ReviewTally>;
  proofingActive: boolean;
  reproofingChapterId?: string | null; // chapter whose background re-proof is running (server)
  onReorder: (files: string[]) => void;   // the list as shown, top to bottom
}) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  // Files chosen but not yet sent: the drop is held here while the reviewer says
  // which of them should be proofed. Nothing uploads until they answer, so a
  // file can be kept out of proofing BEFORE it costs a transcript rather than
  // after it has already started one.
  const [pending, setPending] = useState<File[] | null>(null);
  // Move/copy audio to another project: the selected filenames + the open dialog.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which file is being handed to the browser right now, and how far a bulk run
  // has got. The transfer itself is the browser's — we only mint URLs.
  const [downloading, setDownloading] = useState<string | null>(null);
  const [transfer, setTransfer] = useState<{ files: string[]; mode: "copy" | "move" } | null>(null);

  /** Download one take. The API returns a URL (presigned, in production) and the
   *  browser streams it straight to disk — the bytes never touch the app, so a
   *  600MB take is no different from a small one. */
  async function downloadOne(filename: string) {
    setDownloading(filename);
    try {
      await downloadViaUrl(`/api/projects/${projectId}/audio/${encodeURIComponent(filename)}/download-url`);
    } catch (e) {
      toast((e as Error)?.message || `Couldn't download ${filename}`, "error");
    } finally {
      setDownloading(null);
    }
  }

  /** Download several as ONE zip. Firing a download per file made the browser
   *  ask where to save each one and, past a handful, quietly drop the rest —
   *  so the archive is built and streamed by the server instead, and the
   *  browser sees a single file. */
  async function downloadMany(filenames: string[]) {
    setDownloading("archive");
    try {
      // Omit `files` for the whole delivery; name them for a selected subset.
      const all = filenames.length === orderedAssets.length;
      const qs = all ? "" : "?" + filenames.map((f) => `files=${encodeURIComponent(f)}`).join("&");
      const res = await api<{ url: string; filename: string; count: number }>(
        `/api/projects/${projectId}/audio/archive-url${qs}`);
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast((e as Error)?.message || "Couldn't start the download", "error");
    } finally {
      setDownloading(null);
    }
  }
  // Uploads run in the app-level manager (survive navigation; multi-project).
  const uploads = useUploads();
  const replaceJobs = useReplaceJobs();

  const { data } = useQuery({
    queryKey: ["audio", projectId],
    queryFn: () =>
      api<{ assets: AudioAssetDto[] }>(`/api/projects/${projectId}/audio`).then((r) => r.assets),
    refetchInterval: 2500,
  });
  const assets = useMemo(() => data ?? [], [data]);
  const assetByFile = useMemo(() => new Map(assets.map((a) => [a.filename, a])), [assets]);
  const chapterByFile = useMemo(
    () => new Map(chapters.filter((c) => c.audio_filename).map((c) => [c.audio_filename as string, c])),
    [chapters]
  );

  // Boundary review: which proofed take's Start/End popover is open. When one is
  // open we fetch that take's word-accurate boundary (same source as the report
  // card) so the snippets match exactly — only the open take, only on demand.
  const [openBoundary, setOpenBoundary] = useState<string | null>(null);
  const openChapterId = openBoundary ? chapterByFile.get(openBoundary)?.id : undefined;
  // Correcting a boundary here saves instantly + schedules one background
  // re-proof for the chapter (debounced, batches start+end) — no waiting.
  // statusOf(ch.id) drives the per-file "Re-proofing…" status in the list.
  const { statusOf, scheduleReproof } = useBoundaryReproof(projectId);
  // A chapter is re-proofing if this session is tracking it (instant, covers the
  // debounce too) OR the server reports its re-proof running (survives refresh).
  const isReproofing = (chapterId?: string) =>
    !!chapterId && (statusOf(chapterId) !== "idle" || chapterId === reproofingChapterId);
  const { data: openBoundaryData } = useQuery({
    queryKey: ["boundary", openChapterId],
    queryFn: () =>
      api<Boundary>(`/api/modules/proofing/projects/${projectId}/chapters/${openChapterId}/boundary`),
    enabled: !!openChapterId,
  });

  // Display order of asset rows (filenames). Chaptered files sort by their
  // chapter order_index; the rest keep upload order. Re-syncs when the set of
  // files OR the chapter order changes (so a manual reorder persists, and a
  // background re-map's order is reflected) — but not on every poll.
  const [order, setOrder] = useState<string[]>([]);
  const assetKey = assets.map((a) => `${a.filename}:${a.place ?? ""}`).slice().sort().join("|");
  const chapterOrderKey = chapters.map((c) => `${c.audio_filename}:${c.order_index}`).sort().join("|");
  useEffect(() => {
    // Union of chaptered files (proofed/mapped takes) + uploaded assets, keyed by
    // filename: chaptered first in chapter order, then any extra uploads. A proofed
    // take therefore still shows even if its AudioAsset row is missing (an
    // imported/converted project, or a transiently-empty /audio response).
    setOrder(orderTakes(chapters, assets));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetKey, chapterOrderKey]);

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  function moveRow(from: number, to: number) {
    if (from === to) return;
    const next = order.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setOrder(next);
    onReorder(next);
  }
  // The name a row shows: a corrected delivery reads as the file it replaced.
  const shownOf = (fn: string) =>
    assetByFile.get(fn)?.display_name || chapterByFile.get(fn)?.shown_filename || fn;

  // Sort the list by the name each row shows (numeric-aware, so s2 < s10) —
  // never by a version's storage name — then persist it the same way a drag
  // does, like a one-shot reorder. You can still drag afterward.
  function sortByName(dir: "asc" | "desc") {
    const next = order.slice().sort((a, b) =>
      shownOf(a).localeCompare(shownOf(b), undefined, { numeric: true, sensitivity: "base" }));
    if (dir === "desc") next.reverse();
    setOrder(next);
    onReorder(next);
  }

  // Per-file "Proof" toggle: exclude a bookend/credits file from the manuscript
  // comparison (still spec-checked) or re-include it (re-proofs in the background).
  const proofExclude = useMutation({
    mutationFn: (v: { chapterId: string; excluded: boolean }) =>
      api(`/api/modules/proofing/projects/${projectId}/chapters/${v.chapterId}/proof-excluded`, {
        method: "POST",
        body: JSON.stringify({ excluded: v.excluded }),
      }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["project", projectId] });
      const prev = qc.getQueryData(["project", projectId]);
      qc.setQueryData(["project", projectId], (old: any) =>
        old
          ? { ...old, chapters: old.chapters.map((c: any) => (c.id === v.chapterId ? { ...c, proof_excluded: v.excluded } : c)) }
          : old);
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["project", projectId], ctx.prev);
      toast(e?.message || "Couldn't update that file.", "error");
    },
    onSuccess: (_d, v) => toast(v.excluded ? "Excluded from proofing." : "Re-proofing this file…", "info"),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      qc.invalidateQueries({ queryKey: ["pickups", projectId] }); // excluding removes the take's pickups
    },
  });

  // What is about to be destroyed, held until it is confirmed. One file and
  // thirty-one go the same way: the × used to delete on a single click, with no
  // warning, and it takes a chapter's proofing with it.
  const [deleting, setDeleting] = useState<Doomed[] | null>(null);

  const del = useMutation({
    mutationFn: (names: string[]) =>
      api<{ deleted: string[] }>(`/api/projects/${projectId}/audio/delete`, {
        method: "POST", body: JSON.stringify({ filenames: names }),
      }),
    onSuccess: (r) => {
      const n = r.deleted.length;
      toast(`${n} file${n === 1 ? "" : "s"} removed.`, "success");
      setDeleting(null);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["submission", projectId] });
    },
    onError: (e: any) => toast(e.message || "Couldn't remove those files.", "error"),
  });

  const [replacing, setReplacing] = useState(false);

  // One call sets a colour, a note, or both, across one file or many. The
  // server checks the whole list before writing, so a selection naming a file
  // the project no longer has fails whole rather than half-applying.
  const annotate = useMutation({
    mutationFn: (v: { filenames: string[]; label_color?: string; notes?: string }) =>
      api(`/api/projects/${projectId}/audio/annotate`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["audio", projectId] }),
    onError: (e: any) => toast(e?.message || "That didn't save — please try again.", "error"),
  });
  // Which files a colour pick or a note is about: one row, or the selection.
  const [labelFor, setLabelFor] = useState<{ files: string[]; rect: DOMRect; current?: string; beside?: boolean } | null>(null);
  const [noteFor, setNoteFor] = useState<{ files: string[]; value: string } | null>(null);

  const promote = useMutation({
    mutationFn: (filename: string) =>
      api(`/api/projects/${projectId}/audio/promote`, {
        method: "POST", body: JSON.stringify({ filename }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: (e: any) => toast(e.message || "Couldn't switch version.", "error"),
  });

  // Re-launch the background worker for takes that stalled (transcribed but
  // never proofed because the worker died/errored). Idempotent server-side.
  const resume = useMutation({
    mutationFn: () => api(`/api/projects/${projectId}/proof/resume`, { method: "POST" }),
    onSuccess: () => {
      toast("Resuming proofing…", "info");
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
    },
    onError: (e: any) => toast(e.message || "Couldn't resume proofing.", "error"),
  });

  // This project's in-flight/recent uploads (from the app-level manager).
  const myUploads = useMemo(
    () => uploads.items.filter((u) => u.projectId === projectId),
    [uploads.items, projectId]
  );
  // Rows to show in the table: still queued/uploading, or failed (so you can
  // retry). Done rows vanish — the take appears in the real list via refetch.
  const uploadRows = myUploads.filter(
    (u) => u.status === "queued" || u.status === "uploading" || u.status === "error"
  );

  function addFiles(files: FileList | File[]) {
    const incoming = Array.from(files).filter((f) => AUDIO_RE.test(f.name));
    if (!incoming.length) {
      toast("No accepted audio files (.wav .mp3 .m4a .flac .ogg)", "error");
      return;
    }
    // Skip files already uploaded or already in flight for THIS project (the
    // manager also guards active dups, but this avoids a redundant re-upload).
    const known = new Set([
      ...assets.map((a) => a.filename),
      ...myUploads.filter((u) => u.status === "queued" || u.status === "uploading").map((u) => u.filename),
    ]);
    const fresh = incoming.filter((f) => !known.has(f.name));
    if (!fresh.length) {
      toast("Those files are already here.", "info");
      return;
    }
    // Proofing needs a manuscript to proof against. Without one there is nothing
    // to decide, so asking would be a click that changes nothing - the drop
    // parks and the reviewer picks Spec check (or adds a manuscript) after.
    if (!hasManuscript) {
      const n = uploads.enqueue(projectId, fresh, projectName, false);
      if (n > 0) toast(`Uploading ${n} file${n > 1 ? "s" : ""} — pick a service when ready.`, "info");
      return;
    }
    setPending(fresh);
  }

  /** The drop, once the reviewer has said what each file is for. */
  function sendPending(proof: Set<string>) {
    const files = pending ?? [];
    setPending(null);
    const n = uploads.enqueue(projectId, files, projectName, (f) => proof.has(f.name));
    if (!n) return;
    const p = files.filter((f) => proof.has(f.name)).length;
    toast(
      p === 0 ? `Uploading ${n} file${n > 1 ? "s" : ""} — nothing queued for proofing.`
        : p === n ? `Uploading ${n} file${n > 1 ? "s" : ""} — proofing starts as each one lands.`
          : `Uploading ${n} files — ${p} queued for proofing, ${n - p} stored only.`,
      "info",
    );
  }
  const nProofed = chapters.filter((c) => c.state === "proofed").length;
  // Transcribed takes whose chapter isn't proofed yet. With a live worker these
  // are genuinely "proofing"; with NO worker they're stalled (the proof died).
  const transcribedUnproofed = assets.filter((a) => {
    const c = chapterByFile.get(a.filename);
    // Excluded files are deliberately unproofed — they're not "stalled"/pending.
    return a.status === "transcribed" && c?.state !== "proofed" && !c?.proof_excluded;
  }).length;
  const nUploading = uploadRows.filter((u) => u.status !== "error").length;
  // Parked = neutral drops uploaded with auto-proof off (pending, not requested).
  // They're not "processing" — they wait for "Start proofing" or a Spec check.
  const [startingProof, setStartingProof] = useState<AudioAssetDto[] | null>(null);
  // Noise Analytics: superadmins only, while the detector is still in the Lab
  const { data: me } = useMe();
  const [noiseFor, setNoiseFor] = useState<string[] | null>(null);
  const nWorking =
    nUploading +
    assets.filter((a) => (a.status === "pending" && a.proof_requested !== false) || a.status === "transcribing").length +
    (proofingActive ? transcribedUnproofed : 0);
  const stalledCount = proofingActive ? 0 : transcribedUnproofed;
  // Use the real AudioAsset when present; otherwise a synthetic stand-in so a
  // chaptered take with no asset row still renders (its chapter drives the status,
  // so a proofed take shows "Proofed" + its Report link).
  const orderedAssets: AudioAssetDto[] = order.map(
    (fn): AudioAssetDto =>
      assetByFile.get(fn) ?? { filename: fn, size_bytes: 0, status: "transcribed", error: null, proof_requested: true }
  );

  // Superseded deliveries do not appear as rows - the project holds one file
  // per chapter - but they are in the zip, so the button says so rather than
  // letting a bigger-than-expected download explain itself.
  // One player for the list: starting a second file stops the first, which is
  // the only sane behaviour when every row has a play button.
  const player = useRowPlayer(projectId);

  const earlierCount = orderedAssets.reduce(
    (n, a) => n + (a.versions ?? []).filter((v) => !v.is_current).length, 0);

  // The production's runtime: the official version of every file, summed. This
  // is the figure the workflow record carries, so the hub shows the same one
  // rather than leaving it to be discovered on the board. Partial until every
  // file has been measured, and says so.
  const measured = orderedAssets.filter((a) => !!a.duration_seconds);
  const totalRuntime = measured.reduce((n, a) => n + (a.duration_seconds || 0), 0);
  const unmeasured = orderedAssets.length - measured.length;

  // Move/copy selection: every finished file is eligible (an in-flight upload
  // isn't). Selecting drives the bulk bar; a per-row button transfers just one.
  // Files uploaded without proofing - the ones somebody deliberately left out
  // while they were arriving. They are the only files there is anything to
  // start, so every "start proofing" route below works from this list.
  /** What removing these files would actually cost, so the dialog can say it. */
  const doomedFrom = (names: string[]): Doomed[] => names.map((name) => {
    const a = assetByFile.get(name);
    const ch = chapterByFile.get(name);
    return {
      name,
      runtime: a?.duration_seconds || 0,
      proofed: ch?.state === "proofed",
      // Counted by the server across the whole slot, so it is right before a
      // report exists - which is exactly when a delivery gets cleared out.
      pickups: a?.pickups ?? (ch ? (reviewByChapter[ch.id]?.picked ?? 0) : 0),
      olderVersions: (a?.versions ?? []).filter((v) => !v.is_current).length,
    };
  });

  // A replacement carrying its predecessor's proof is parked too, but it has
  // nothing left to start - proofing it again would pay for a transcript of
  // work the project already has.
  const parkedList = useMemo(
    () => parkedFiles(orderedAssets).filter((a) => chapterByFile.get(a.filename)?.state !== "proofed"),
    [orderedAssets, chapterByFile],
  );
  const parked = parkedList.length;

  const selectableFiles = orderedAssets
    .filter((a) => a.status !== "transcribing" && a.status !== "failed")
    .map((a) => a.filename);
  const selectedFiles = selectableFiles.filter((f) => selected.has(f));
  // Only the parked ones can be STARTED; a file already proofing or proofed has
  // nothing to start, so the button counts what it would actually do.
  const selectedParked = parkedList.filter((a) => selected.has(a.filename));
  const allSelected = selectableFiles.length > 0 && selectedFiles.length === selectableFiles.length;
  // Where a Shift-range starts: the last box ticked WITHOUT shift.
  const anchor = useRef<number | null>(null);

  /** Ticking, with the ranges a file list is expected to have: a plain click
   *  sets one and anchors there, Shift extends the anchor's run.
   *
   *  Indexed by RENDERED position, not by filename — the list is drag-reorderable
   *  and sortable, so "everything between these two" means what is between them
   *  on screen, which is the only range the user can see themselves making.
   *
   *  Rows that cannot be selected (transcribing, failed) are stepped over rather
   *  than silently breaking the run: dragging across a file that happens to be
   *  mid-transcribe should not stop the selection at it. */
  const tickFile = (index: number, e: ReactMouseEvent<HTMLInputElement>) => {
    const fn = orderedAssets[index]?.filename;
    if (!fn) return;
    const want = !selected.has(fn);
    setSelected((s) => {
      const n = new Set(s);
      if (e.shiftKey && anchor.current !== null) {
        const [lo, hi] = anchor.current < index ? [anchor.current, index] : [index, anchor.current];
        for (let k = lo; k <= hi; k++) {
          const name = orderedAssets[k]?.filename;
          if (!name || !selectableFiles.includes(name)) continue;
          if (want) n.add(name); else n.delete(name);
        }
      } else {
        if (want) n.add(fn); else n.delete(fn);
        anchor.current = index;
      }
      return n;
    });
  };

  const toggleAll = () => {
    // Select-all is not a place to extend from — the next Shift-click would
    // otherwise reach back to whatever was ticked before it.
    anchor.current = null;
    setSelected(allSelected ? new Set() : new Set(selectableFiles));
  };
  // Fit / Full, the same choice the board offers. Fit keeps the card inside the
  // page's reading width; Full lets it use the whole main area, which is the
  // difference between scrolling for the last column and not.
  const [wide, setWide] = useState(() => localStorage.getItem(TAKES_WIDE_KEY) === "1");
  // On by default: a note you cannot see is a note you will not remember
  // leaving. Hidden is the deliberate choice, and the count says what it costs.
  const [notesOn, setNotesOn] = useState(() => localStorage.getItem(TAKES_NOTES_KEY) !== "0");
  useEffect(() => { try { localStorage.setItem(TAKES_NOTES_KEY, notesOn ? "1" : "0"); } catch { /* ignore */ } }, [notesOn]);
  const noted = orderedAssets.filter((a) => (a.notes || "").trim()).length;
  useEffect(() => { try { localStorage.setItem(TAKES_WIDE_KEY, wide ? "1" : "0"); } catch { /* ignore */ } }, [wide]);
  // The page owns its own max-width, so widening has to happen there — the card
  // can't grow past a container that is centring it. Cleaned up on unmount so
  // leaving the hub in Full doesn't leave every other page wide.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const page = cardRef.current?.closest(".page");
    if (!page) return;
    page.classList.toggle("takes-wide", wide);
    return () => page.classList.remove("takes-wide");
  }, [wide]);

  const anyProofedOf = (fs: string[]) => fs.some((f) => chapterByFile.get(f)?.state === "proofed");
  // Files proofed while the script's notes counted as text (script check).
  const scriptCheck = useScriptCheck(projectId);
  const notesAsText = new Set(
    scriptCheck.data?.state === "needed" ? (scriptCheck.data.files ?? []).map((f) => f.chapter_id) : [],
  );
  const openTransfer = (fs: string[], mode: "copy" | "move") => { if (fs.length) setTransfer({ files: fs, mode }); };

  return (
    <div className="card takes-card" ref={cardRef} style={{ padding: 0, overflow: "hidden" }}>
      {/* A card header, not a section header: this card sits INSIDE Assets, so
          it must read quieter than the "Assets" heading above it. The h3 here
          previously carried no size rule and fell through to the 19px base,
          which made the contents shout over their own container. */}
      <div className="card-head">
        <div className="card-head-title">
          <strong>Audio Files ({orderedAssets.length + uploadRows.length})</strong>
          {totalRuntime > 0 && (
            <span className="takes-total mono"
              title={unmeasured > 0
                ? `The ${measured.length} file${measured.length === 1 ? "" : "s"} measured so far. ${unmeasured} still to measure.`
                : "Total runtime of this production - the official version of every file"}>
              {formatTime(totalRuntime)}
              {unmeasured > 0 && <em> so far</em>}
            </span>
          )}
        </div>
        <span className="card-head-right">
          {nWorking > 0 ? (
            <><span className="spinner" style={{ width: 12, height: 12 }} />{nWorking} processing…</>
          ) : parked > 0 ? (
            <>
              <span>{parked} uploaded, not proofed</span>
              {hasManuscript && (
                <button className="btn sm primary" onClick={() => setStartingProof(parkedList)}
                  title="Transcribe and compare these against the manuscript">
                  <Icon name="play" size={14} /> Start proofing…
                </button>
              )}
            </>
          ) : stalledCount > 0 ? (
            <>
              <span className="take-stalled">⚠ {stalledCount} stalled</span>
              <button className="btn sm" disabled={resume.isPending} onClick={() => resume.mutate()}
                title="Re-run proofing for audio files that didn't finish (the worker stopped before they were proofed).">
                <Icon name="rotate-ccw" size={14} /> Resume proofing
              </button>
            </>
          ) : nProofed > 0 ? (
            `${nProofed} proofed`
          ) : (
            "Add audio files below"
          )}
          {nUploading > 0 && (
            <label className="email-when-done"
              title="We'll email your account when this project's uploads finish — handy for big batches you walk away from.">
              <input type="checkbox"
                checked={uploads.notifyArmed.includes(projectId)}
                onChange={(e) => uploads.setNotifyOnDone(projectId, projectName, e.target.checked)} />
              Email me when done
            </label>
          )}
        </span>
      </div>

      <ScriptReproofBanner projectId={projectId} inCard />

      <div
        className={`dropzone ${drag ? "drag" : ""}`}
        style={{ margin: "0 16px 14px" }}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
      >
        <input
          ref={fileInput}
          type="file"
          accept="audio/*,.wav,.mp3,.m4a,.flac,.ogg,.webm"
          multiple
          hidden
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.currentTarget.value = ""; }}
        />
        <div className="hint">
          {hasManuscript
            ? "Drop chapter audio here, or click to choose — you'll pick which files to proof before anything is sent"
            : "Drop audio here, or click to choose — we'll store it; pick Spec check (or add a manuscript to proof) below"}
        </div>
      </div>

      <audio {...player.audioProps} />

      {/* The colour picker. Anchored to whatever opened it — a row's swatch or
          the bulk bar's button — so the choice appears where the eye already is. */}
      {labelFor && (
        <>
          <div className="lbl-catch" onClick={() => setLabelFor(null)} />
          <div className="lbl-pop" role="dialog" aria-label="Pick a label colour"
            style={anchored(labelFor.rect, {
              width: 182,
              // From the side rail, hang it beside the rail rather than over it.
              offsetLeft: labelFor.beside ? labelFor.rect.width + 14 : 0,
            })}>
            <div className="lbl-pop-h">
              {labelFor.files.length === 1 ? "Label this file" : `Label ${labelFor.files.length} files`}
            </div>
            {LABELS.map((l) => (
              <button key={l.key || "none"} className={`lbl-opt${labelFor.current === l.key ? " on" : ""}`}
                onClick={() => {
                  annotate.mutate({ filenames: labelFor.files, label_color: l.key });
                  setLabelFor(null);
                }}>
                <span className={`lbl-swatch${l.dot ? " on" : ""}`}
                  style={l.dot ? { background: l.dot, borderColor: l.dot } : undefined} />
                {l.name}
              </button>
            ))}
          </div>
        </>
      )}

      {noteFor && (
        <NoteEditor
          value={noteFor.value}
          label={noteFor.files.length === 1
            ? noteFor.files[0]
            : `${noteFor.files.length} files — this replaces whatever note each of them has.`}
          onSave={(v) => { annotate.mutate({ filenames: noteFor.files, notes: v }); setNoteFor(null); }}
          onClose={() => setNoteFor(null)}
        />
      )}

      {deleting && (
        <DeleteFilesDialog files={deleting} busy={del.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => del.mutate(deleting.map((d) => d.name))} />
      )}

      {startingProof && (
        <StartProofingDialog projectId={projectId} files={startingProof}
          onClose={() => setStartingProof(null)}
          onStarted={() => setSelected(new Set())} />
      )}

      {noiseFor && (
        <NoiseAnalyticsDialog projectId={projectId} filenames={noiseFor} onClose={() => setNoiseFor(null)} />
      )}

      {pending && (
        <UploadIntentDialog files={pending} onCancel={() => setPending(null)} onConfirm={sendPending} />
      )}

      {/* A floating vertical rail against the left edge of the page, so the
          actions stay in reach while you scroll a long list — no trip back to
          the top to act, check, then clear. It goes when the selection does. */}
      {selected.size > 0 && (
        <div className="bulk-bar" role="toolbar" aria-orientation="vertical" aria-label="Selected files">
          <span className="bulk-count"><b>{selectedFiles.length}</b> selected</span>
          <button className="btn sm" title="Copy these files to another project" onClick={() => openTransfer(selectedFiles, "copy")}>
            <Icon name="copy" size={14} /><span className="bulk-lbl">Copy to…</span>
          </button>
          <button className="btn sm" title="Move these files to another project" onClick={() => openTransfer(selectedFiles, "move")}>
            <Icon name="transfer" size={14} /><span className="bulk-lbl">Move to…</span>
          </button>
          <button className="btn sm" disabled={!!downloading}
            title="Download the selected files as a single zip"
            onClick={() => downloadMany(selectedFiles)}>
            <Icon name="download" size={14} /><span className="bulk-lbl">Download</span>
          </button>
          {hasManuscript && selectedParked.length > 0 && (
            <button className="btn sm primary"
              title={`Transcribe and compare ${selectedParked.length === 1 ? "this file" : "these files"} against the manuscript`}
              onClick={() => setStartingProof(selectedParked)}>
              <Icon name="play" size={14} /><span className="bulk-lbl">Proof {selectedParked.length === selectedFiles.length ? "selected" : `${selectedParked.length} of these`}</span>
            </button>
          )}
          {me?.is_superadmin && (
            <button className="btn sm"
              title="Score these files with a Noise Lab model, then review its flags on the Noise Analytics tab"
              onClick={() => setNoiseFor(selectedFiles)}>
              <Icon name="flask" size={14} /><span className="bulk-lbl">Noise Analytics</span>
            </button>
          )}
          <button className="btn sm"
            title="Put a colour label on these files"
            onClick={(e) => setLabelFor({
              files: selectedFiles,
              rect: (e.currentTarget as HTMLElement).getBoundingClientRect(),
              // Vertical rail on desktop; on phones it's docked along the bottom.
              beside: window.matchMedia("(min-width: 721px)").matches,
            })}>
            <span className="lbl-swatch bulkdot" /><span className="bulk-lbl">Label</span>
          </button>
          <button className="btn sm"
            title={selectedFiles.length === 1 ? "Add a note to this file" : "Put the same note on these files"}
            onClick={() => setNoteFor({
              files: selectedFiles,
              // Prefilled only when they already agree — otherwise the box would
              // arrive holding one file's note and quietly overwrite the rest.
              value: (() => {
                const notes = selectedFiles.map((f) => orderedAssets.find((a) => a.filename === f)?.notes || "");
                return notes.every((n) => n === notes[0]) ? notes[0] : "";
              })(),
            })}>
            <Icon name="edit" size={14} /><span className="bulk-lbl">Note</span>
          </button>
          <button className="btn sm danger"
            title="Remove these files, their proofing and every version of them"
            onClick={() => setDeleting(doomedFrom(selectedFiles))}>
            <Icon name="trash" size={14} /><span className="bulk-lbl">Delete</span>
          </button>
          <button className="btn ghost sm" title="Clear the selection" onClick={() => setSelected(new Set())}>
            <Icon name="x" size={14} /><span className="bulk-lbl">Clear</span>
          </button>
        </div>
      )}

      {orderedAssets.length > 0 && (
        <div className="takes-sort">
          <button className="btn ghost sm" title="Bring in a corrected delivery — the files you have now are kept"
            // One replacement per project at a time: one under way (or paused)
            // opens instead of a second review of the same files.
            onClick={() => (replaceJobs.get(projectId) ? replaceJobs.show(projectId) : setReplacing(true))}>
            <Icon name="rotate-cw" size={14} />
            Replace file set
          </button>
          <button className="btn ghost sm" disabled={!!downloading}
            title={`Download all ${orderedAssets.length} audio files as a single zip`
              + (earlierCount
                  ? `. ${earlierCount} earlier version${earlierCount === 1 ? "" : "s"} travel${earlierCount === 1 ? "s" : ""} too, in an "Earlier versions" folder.`
                  : "")}
            onClick={() => downloadMany(orderedAssets.map((a) => a.filename))}>
            <Icon name="download" size={14} />
            {downloading === "archive" ? "Preparing zip…" : `Download all (${orderedAssets.length})`}
          </button>
          <span className="takes-sort-sep" />
        </div>
      )}

      {orderedAssets.length > 1 && (
        <div className="takes-sort">
          <span className="muted">Sort by name</span>
          <button className="btn ghost sm" title="Sort A → Z" onClick={() => sortByName("asc")}>A → Z</button>
          <button className="btn ghost sm" title="Sort Z → A" onClick={() => sortByName("desc")}>Z → A</button>
          <span className="muted takes-sort-hint">· or drag rows to reorder</span>
          <button className={`takes-toggle${notesOn ? " on" : ""}`}
            onClick={() => setNotesOn((v) => !v)}
            title={notesOn ? "Hide the notes column" : "Show the notes column — nothing written is lost"}>
            <Icon name="edit" size={13} />
            Notes
            {/* Only when hidden: while the column is up you can see for yourself. */}
            {!notesOn && noted > 0 && <span className="takes-toggle-n">{noted}</span>}
          </button>
          <span className="takes-width">
            <span className="muted">Width</span>
            <span className="seg-sm" role="group" aria-label="Table width">
              <button className={!wide ? "on" : ""} onClick={() => setWide(false)}
                title="Keep the table inside the page">Fit</button>
              <button className={wide ? "on" : ""} onClick={() => setWide(true)}
                title="Use the full width of the window">Full</button>
            </span>
          </span>
        </div>
      )}

      {(orderedAssets.length > 0 || uploadRows.length > 0) && (
        /* The table was never narrow — it was CLIPPED. It already overflowed the
           card, and the card's `overflow: hidden` (there for the rounded corners)
           cut the last column off rather than letting anyone reach it. A scroll
           box is the whole fix; the width toggle above just decides how often
           you need it. */
        <div className="takes-scroll">
        <table className="table takes-table">
          <thead>
            <tr>
              <th className="selcol">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  disabled={selectableFiles.length === 0} aria-label="Select all" title="Select all" />
              </th>
              <th></th><th>File</th>{notesOn && <th className="notecol">Notes</th>}<th className="rtcol">Runtime</th><th className="playcol">Play</th><th>Status</th><th>Pages</th><th>Review</th><th>Proof</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orderedAssets.map((a, i) => {
              const ch = chapterByFile.get(a.filename);
              const proofed = ch?.state === "proofed";
              const review = !!(proofed && ch && ch.boundary_status === "review");
              const expanded = review && openBoundary === a.filename;
              return (
                <Fragment key={a.filename}>
                <tr
                  draggable
                  onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
                  onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) moveRow(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`ch-row${dragIdx === i ? " dragging" : ""}${overIdx === i && dragIdx !== null && dragIdx !== i ? " drag-over" : ""}`}
                >
                  <td className="selcol" onClick={(e) => e.stopPropagation()}>
                    {a.status !== "transcribing" && a.status !== "failed" && (
                      <input type="checkbox" checked={selected.has(a.filename)}
                        // The work happens on click, which is where the modifier
                        // key is; onChange stays so React keeps the input
                        // controlled, and so the keyboard still toggles.
                        onChange={() => { /* see onClick */ }}
                        onClick={(e) => tickFile(i, e)}
                        // Shift-clicking inside a table otherwise paints a text
                        // selection across the rows it spans.
                        onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                        aria-label={`Select ${a.filename}`} />
                    )}
                  </td>
                  <td className="grip" title="Drag to reorder"><Icon name="grip" size={15} /></td>
                  {/* The flex layout lives on a DIV inside the cell, never on
                      the <td>. A table cell set to display:flex stops being a
                      table cell — it leaves the row's height and the column's
                      width behind and paints its own box, which is why the name
                      sat in a white rectangle offset from its own row. */}
                  <td className="cellname" onClick={(e) => e.stopPropagation()}>
                    <div className="cellname-in">
                      <LabelSwatch color={a.label_color}
                        onPick={(rect) => setLabelFor({ files: [a.filename], rect, current: a.label_color })} />
                      <span className="cellname-t">{a.display_name || a.filename}</span>
                      <VersionTag a={a} busy={promote.isPending} onPromote={(f) => promote.mutate(f)} />
                      {/* Hidden Notes still has to be visible as a FACT: a dot
                          where the note would be says this row is carrying one,
                          without giving the column back its width. */}
                      {!notesOn && (a.notes || "").trim() && (
                        <span className="note-dot" title={a.notes}
                          onClick={() => setNoteFor({ files: [a.filename], value: a.notes || "" })} />
                      )}
                    </div>
                  </td>
                  {notesOn && (
                    <td className="notecol" onClick={(e) => e.stopPropagation()}>
                      <button type="button"
                        className={`takes-note${a.notes ? "" : " empty"}`}
                        title={a.notes || "Add a note"}
                        onClick={() => setNoteFor({ files: [a.filename], value: a.notes || "" })}>
                        {a.notes ? (a.notes.split("\n")[0]) : <Icon name="edit" size={13} />}
                      </button>
                    </td>
                  )}
                  <td className="rtcol mono">
                    {player.file === a.filename ? (
                      // Where the playhead is, against the length. Stays put on
                      // pause, so you can stop on a suspect moment and read the
                      // timecode you'd quote to the producer.
                      <span title={`${formatTime(player.at)} of ${formatTime(a.duration_seconds || 0)}`}>
                        <b className="rowplay-at">{formatTime(player.at)}</b>
                        <span className="muted">{a.duration_seconds ? ` / ${formatTime(a.duration_seconds)}` : ""}</span>
                      </span>
                    ) : a.duration_seconds
                      ? formatTime(a.duration_seconds)
                      : <span className="muted" title="Measured from the file's header as soon as it lands. A file still uploading, or one whose header could not be read, shows nothing.">–</span>}
                  </td>
                  <td className="playcol">
                    <RowTransport file={a.filename} player={player} />
                  </td>
                  <td><StatusCell a={a} ch={ch} proofingActive={proofingActive} reproofing={isReproofing(ch?.id)}
                    notesAsText={!!ch && notesAsText.has(ch.id)} /></td>
                  <td className="muted">
                    {proofed && ch ? (
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        {ch.credits
                          ? <span title="The manuscript doesn't have these words, so they weren't compared to it. Their report has one flag asking you to listen to them.">
                              {ch.credits === "end" ? "End credits" : "Opening credits"}
                            </span>
                          : <span>{ch.pages[0]}–{ch.pages[1]}</span>}
                        {review && (
                          <button
                            type="button"
                            className={`boundary-chip${expanded ? " open" : ""}`}
                            title="Optional — double-check where this file starts and ends in the manuscript. The first take's start and the last take's end have no neighbouring take to vouch for them, so they're offered for a look."
                            onClick={() => setOpenBoundary(expanded ? null : a.filename)}
                          >
                            Start / End <Icon name="chevron-down" size={12} className="bchip-chev" />
                          </button>
                        )}
                      </span>
                    ) : "—"}
                  </td>
                  <td>{proofed && ch ? <ReviewCell t={reviewByChapter[ch.id]} /> : <span className="muted">—</span>}</td>
                  <td>
                    {ch ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!ch.proof_excluded}
                        className={`proof-toggle${ch.proof_excluded ? "" : " on"}`}
                        title={ch.proof_excluded
                          ? "Excluded from proofing — not compared to the manuscript (still spec-checked). Switch on to proof it."
                          : "Proofing this file against the manuscript. Switch off for files with no script — e.g. opening / closing credits or a production announcement. They’re skipped here but still checked in Spec check."}
                        disabled={proofExclude.isPending}
                        onClick={() => proofExclude.mutate({ chapterId: ch.id, excluded: !ch.proof_excluded })}
                      >
                        <span className="proof-toggle-track"><span className="proof-toggle-knob" /></span> Proof
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {proofed && ch && (
                      <Link className="btn ghost sm" to={`/projects/${projectId}/report/${ch.id}`}>Report →</Link>
                    )}
                    {a.status !== "transcribing" && (
                      <button className="btn ghost sm" title={`Download ${a.filename}`}
                        disabled={downloading === a.filename}
                        onClick={() => downloadOne(a.filename)}><Icon name="download" size={14} /></button>
                    )}
                    {a.status !== "transcribing" && a.status !== "failed" && (
                      <button className="btn ghost sm" title="Move or copy to another project"
                        onClick={() => openTransfer([a.filename], "copy")}><Icon name="transfer" size={14} /></button>
                    )}
                    {a.status !== "transcribing" && (
                      <button className="btn ghost sm" title="Remove file"
                        onClick={() => setDeleting(doomedFrom([a.filename]))}>×</button>
                    )}
                  </td>
                </tr>
                {expanded && ch && (
                  <tr className="boundary-expand">
                    <td colSpan={10}>
                      {openBoundaryData ? (
                        <>
                          <div className="boundary-cards">
                            <BoundaryCard
                              projectId={projectId} chapterId={ch.id} which="start"
                              side={openBoundaryData.start} pdfUrl={openBoundaryData.pdf_url} offset={openBoundaryData.offset} compact
                              onSaved={() => scheduleReproof(ch.id)}
                            />
                            <BoundaryCard
                              projectId={projectId} chapterId={ch.id} which="end"
                              side={openBoundaryData.end} pdfUrl={openBoundaryData.pdf_url} offset={openBoundaryData.offset} compact
                              onSaved={() => scheduleReproof(ch.id)}
                            />
                          </div>
                          {statusOf(ch.id) !== "idle" && (
                            <div className="reproof-hint" role="status">
                              <span className="reproof-dot" />
                              {statusOf(ch.id) === "pending"
                                ? "Boundary saved — re-proofing this chapter shortly. It runs in the background."
                                : "Re-proofing this chapter in the background."}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="muted" style={{ padding: "6px 2px", fontSize: 13 }}>Loading boundary…</div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {uploadRows.map((u) => (
              <tr key={"upload:" + u.id} className="ch-row">
                <td className="selcol" />
                <td className="grip" />
                <td className="cellname">{u.filename}</td>
                {/* Nothing to measure or play until the bytes are all here. */}
                <td className="rtcol muted">–</td>
                <td className="playcol" />
                <td>
                  {u.status === "queued" ? (
                    <span className="take-working">Queued…</span>
                  ) : u.status === "error" ? (
                    <span className="take-failed" title={u.error || ""}>
                      Upload failed
                      {u.error && <span className="take-failed-why">{u.error}</span>}
                    </span>
                  ) : (
                    <span className="take-working" style={{ gap: 8 }}>
                      uploading {u.pct}%
                      <span className="progress-track" style={{ width: 70, display: "inline-block" }}>
                        <span className="progress-fill" style={{ width: `${u.pct}%`, display: "block", height: "100%" }} />
                      </span>
                    </span>
                  )}
                </td>
                <td className="muted">—</td>
                <td className="muted">—</td>
                <td className="muted">—</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {u.status === "error" ? (
                    <button className="btn ghost sm" title="Retry upload" onClick={() => uploads.retry(u.id)}>↻</button>
                  ) : (
                    <button className="btn ghost sm" title="Cancel upload" onClick={() => uploads.cancel(u.id)}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {replacing && (
        <ReplaceFileSetDialog
          projectId={projectId}
          assets={assets}
          onClose={() => setReplacing(false)}
        />
      )}

      {transfer && (
        <TransferAudioDialog
          projectId={projectId}
          files={transfer.files}
          mode={transfer.mode}
          anyProofed={anyProofedOf(transfer.files)}
          onClose={() => setTransfer(null)}
          onDone={() => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ["audio", projectId] }); }}
        />
      )}
    </div>
  );
}
